// The one thing the قرعه‌کشی landing asks for: a number to call back on.
//
// Three ways to get it, and the order matters — a fund manager reading this on
// a phone inside a messenger should not be made to type a number the messenger
// already knows:
//
//   1. **Signed in already.** Nothing is asked. The request carries the
//      session and the backend reads the number off the member.
//   2. **Inside Eitaa or Telegram.** `requestContact()` returns a payload the
//      messenger itself signed, so one tap on a native sheet proves the number
//      with no typing and no SMS. Bale's SDK hands back a bare unsigned string,
//      which is fine as a pre-fill and is never treated as proof.
//   3. **Typed.** Always available, never hidden behind the other two — the
//      native sheet can be declined, rate-limited (Bale caps it at three per
//      session) or simply absent on the open web.
//
// The field stays reachable in every one of those states on purpose. A flow
// whose only path is a native sheet is a flow that dead-ends the moment the
// sheet refuses, and this one has exactly one job.

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
    canRequestContact,
    getInitData,
    requestPhoneFromMessenger,
    toLocalMobile,
} from 'modules/miniapp'
import { requestFundTransfer } from 'services/miniapp'

interface Props {
    code: string | null
    /** Pre-filled from the messenger profile when we have one. */
    name?: string | null
    onClose: () => void
    onDone: (via: string) => void
    /** Reported so the funnel can tell a declined sheet from a typed number. */
    onEvent: (event: string, props?: Record<string, unknown>) => void
}

type Phase = 'asking' | 'sending'

const isValidMobile = (raw: string) => {
    const local = toLocalMobile(raw)
    return !!local && /^09\d{9}$/.test(local)
}

export default function TransferSheet({
    code,
    name,
    onClose,
    onDone,
    onEvent,
}: Props) {
    const [phase, setPhase] = useState<Phase>('asking')
    const [phone, setPhone] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [canShare, setCanShare] = useState(false)
    // Guards the async capability probe: the sheet can be dismissed while
    // `canRequestContact()` is still loading the messenger SDK, and setting
    // state after that is a warning in dev and a leak in principle.
    const alive = useRef(true)

    useEffect(() => {
        alive.current = true
        canRequestContact()
            .then((ok) => {
                if (alive.current) setCanShare(ok)
            })
            .catch(() => {
                /* not in a messenger, or the SDK never loaded */
            })
        return () => {
            alive.current = false
        }
    }, [])

    const submit = async (
        payload: { phone?: string; contactResponse?: string },
        via: string
    ) => {
        setPhase('sending')
        setError(null)
        try {
            await requestFundTransfer({
                code,
                name,
                initData: getInitData(),
                ...payload,
            })
            onEvent('lottery_landing_phone_captured', { via })
            onDone(via)
        } catch {
            // Deliberately not "try again later": this person just asked to be
            // called and the only useful thing now is the field they can type
            // into.
            setError('ثبت نشد. لطفاً شماره را دستی وارد کنید و دوباره بزنید.')
            setPhase('asking')
        }
    }

    const share = async () => {
        onEvent('lottery_landing_contact_requested', {})
        setError(null)
        const result = await requestPhoneFromMessenger()
        if (!result) {
            onEvent('lottery_landing_contact_declined', {})
            setError('شماره‌ای به ما داده نشد. می‌توانید دستی واردش کنید.')
            return
        }
        // A signed payload is proof; a bare string is only a pre-fill, so it
        // goes into the field for the user to confirm rather than being sent
        // as if the messenger had vouched for it.
        if (result.signedResponse) {
            return submit({ contactResponse: result.signedResponse }, 'contact')
        }
        if (result.phone) {
            setPhone(result.phone)
            setError(null)
            return
        }
        setError('شماره‌ای به ما داده نشد. می‌توانید دستی واردش کنید.')
    }

    const sending = phase === 'sending'

    return createPortal(
        <div
            className="fixed inset-0 z-[950] flex items-end"
            role="dialog"
            aria-modal="true"
            aria-label="ثبت درخواست انتقال صندوق"
        >
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div
                className="relative w-full bg-white rounded-t-[16px] p-4"
                style={{ paddingBottom: 'calc(16px + var(--safe-bottom))' }}
            >
                <p className="text-base font-bold text-black">
                    شماره‌تان را داشته باشیم
                </p>
                <p className="text-sm text-text-secondary leading-6 mt-2">
                    انتقال صندوق را با هم انجام می‌دهیم — اعضا، سهم‌ها و
                    نوبت‌هایی که تا حالا کشیده‌اید. فقط بگویید به کجا زنگ بزنیم.
                </p>

                {canShare && (
                    <button
                        type="button"
                        disabled={sending}
                        onClick={share}
                        className="w-full h-12 mt-4 rounded-[10px] bg-green-1 text-white text-sm font-bold active:scale-[0.99] disabled:opacity-60"
                    >
                        تأیید با شمارهٔ همین پیام‌رسان
                    </button>
                )}

                <label
                    className="block text-xs text-text-secondary mt-4 mb-1"
                    htmlFor="landing-transfer-phone"
                >
                    {canShare ? 'یا شماره را وارد کنید' : 'شمارهٔ همراه'}
                </label>
                <input
                    id="landing-transfer-phone"
                    // `tel` and not `number`: a number spinner on a phone field
                    // is meaningless, and `number` silently drops the leading
                    // zero every Iranian mobile starts with.
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    dir="ltr"
                    placeholder="09xxxxxxxxx"
                    value={phone}
                    disabled={sending}
                    onChange={(e) => {
                        setPhone(e.target.value)
                        setError(null)
                    }}
                    className="w-full h-12 rounded-[10px] border border-divider px-3 text-sm text-black text-left placeholder:text-text-secondary"
                />

                {error && (
                    <p className="text-xs text-[#E0564C] mt-2 leading-5">
                        {error}
                    </p>
                )}

                <button
                    type="button"
                    disabled={sending || !isValidMobile(phone)}
                    onClick={() =>
                        submit(
                            { phone: toLocalMobile(phone) || phone },
                            'typed'
                        )
                    }
                    className={`w-full h-12 mt-3 rounded-[10px] text-sm font-bold active:scale-[0.99] ${
                        canShare
                            ? 'bg-[#F3F9EF] text-[#3B7A25] border border-[#CDE3BF]'
                            : 'bg-green-1 text-white'
                    } disabled:opacity-50`}
                >
                    {sending ? 'در حال ثبت…' : 'ثبت درخواست'}
                </button>
                <button
                    type="button"
                    disabled={sending}
                    onClick={onClose}
                    className="w-full h-10 mt-1 text-sm text-text-secondary"
                >
                    بعداً
                </button>
            </div>
        </div>,
        document.body
    )
}
